<template>
  <div :class="['ui-select', sizeClass, { 'ui-select--disabled': disabled }]">
    <select
      :id="id"
      ref="selectRef"
      :value="modelValue"
      :disabled="disabled"
      :required="required"
      :name="name"
      :aria-label="ariaLabel"
      :aria-invalid="invalid || undefined"
      class="ui-select__field"
      v-bind="$attrs"
      @change="onChange"
    >
      <!-- Allows callers to inject a leading null/placeholder option,
           mirroring BootstrapVue-Next's `b-form-select` `#first` slot. -->
      <slot name="first" />
      <option
        v-for="opt in normalizedOptions"
        :key="String(opt.value)"
        :value="opt.value"
        :disabled="opt.disabled"
      >
        {{ opt.text }}
      </option>
    </select>
  </div>
</template>

<script setup lang="ts">
  /**
   * UiSelect — Tailwind primitive replacing BootstrapVue-Next's
   * `b-form-select`.
   *
   * Visually matches UiInput (gray-300 border, primary-600 focus ring,
   * sm/md/lg sizes) with a custom SVG chevron in place of the UA arrow.
   *
   * Usage:
   *   <UiSelect v-model="country" :options="countryOptions" />
   *
   *   <!-- Leading placeholder/null option (BVN parity) -->
   *   <UiSelect v-model="period" :options="periods">
   *     <template #first>
   *       <option :value="null">- Select period -</option>
   *     </template>
   *   </UiSelect>
   *
   * Searchable / multi-select / async-loaded selects are out of scope —
   * those map to a heavier vue-select-style component.
   */
  import { ref, computed } from 'vue'

  defineOptions({ name: 'UiSelect', inheritAttrs: false })

  type OptionValue = string | number | boolean | null
  type RichOption = { value: OptionValue, text: string, disabled?: boolean }
  type RawOption = OptionValue | RichOption

  interface Props {
    modelValue?: OptionValue
    options?: RawOption[]
    disabled?: boolean
    required?: boolean
    size?: 'sm' | 'md' | 'lg'
    id?: string
    name?: string
    ariaLabel?: string
    invalid?: boolean
    /**
     * Key to read each option's value from. Mirrors b-form-select's
     * `value-field` so callers can keep their existing data shapes
     * (e.g. `travellerEmployeeNumber`) without re-mapping.
     */
    valueField?: string
    /** Key to read each option's display text from. */
    textField?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    options: () => [],
    disabled: false,
    required: false,
    size: 'md',
    id: undefined,
    name: undefined,
    ariaLabel: undefined,
    invalid: false,
    valueField: 'value',
    textField: 'text',
  })

  const emit = defineEmits<{
    'update:modelValue': [value: OptionValue]
    'change': [value: OptionValue]
  }>()

  const selectRef = ref<HTMLSelectElement | null>(null)

  const sizeClass = computed(() => `ui-select--${props.size}`)

  // TS narrowing across `RawOption = OptionValue | RichOption` doesn't
  // contract `opt` back to a primitive in the else-branch on its own —
  // an explicit type guard is the cheapest way to keep `vue-tsc` happy.
  function isRichOption (opt: RawOption): opt is RichOption {
    return opt !== null && typeof opt === 'object'
  }

  // Accept primitive arrays (`['Yes', 'No']`) or rich object shapes —
  // same affordance as b-form-select. `valueField` / `textField` let
  // callers keep arbitrary key names (e.g. `travellerName`).
  const normalizedOptions = computed<RichOption[]>(() =>
    (props.options ?? []).map<RichOption>(opt => {
      if (isRichOption(opt)) {
        const obj = opt as unknown as Record<string, unknown>
        return {
          value: obj[props.valueField] as OptionValue,
          text: String(obj[props.textField] ?? ''),
          disabled: obj.disabled === true,
        }
      }
      return { value: opt, text: String(opt) }
    })
  )

  // Coerce numeric strings back to numbers when the original option's
  // value was numeric — `<select>` always reports strings, but callers
  // expect their `v-model` to round-trip the same type.
  function coerce (raw: string): OptionValue {
    const match = normalizedOptions.value.find(o => String(o.value) === raw)
    return match ? match.value : raw
  }

  function onChange (event: Event) {
    const value = coerce((event.target as HTMLSelectElement).value)
    emit('update:modelValue', value)
    emit('change', value)
  }

  defineExpose({
    focus: () => selectRef.value?.focus(),
    blur: () => selectRef.value?.blur(),
    select: selectRef,
  })
</script>

<style scoped>
  .ui-select {
    position: relative;
    display: flex;
    align-items: stretch;
    width: 100%;
  }

  .ui-select__field {
    flex: 1 1 auto;
    width: 100%;
    border: 1px solid #d1d5db;          /* gray-300 */
    border-radius: 0.375rem;
    background-color: #fff;
    color: #111827;                     /* gray-900 */
    font-size: 0.875rem;
    line-height: 1.25rem;
    transition: border-color 150ms ease, box-shadow 150ms ease;
    /* Strip the UA chevron and substitute our own SVG so the control
       matches UiInput visually across browsers. */
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image:
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.5rem center;
    background-size: 1rem 1rem;
    cursor: pointer;
  }
  .ui-select__field:focus {
    outline: none;
    border-color: #2563eb;              /* primary-600 */
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
  .ui-select__field:disabled {
    background-color: #f9fafb;          /* gray-50 */
    color: #6b7280;                     /* gray-500 */
    cursor: not-allowed;
  }
  .ui-select__field[aria-invalid="true"] {
    border-color: #dc2626;              /* red-600 */
  }
  .ui-select__field[aria-invalid="true"]:focus {
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
  }

  /* Sizes — heights/font sizes mirror UiInput, with extra right padding
     to make room for the chevron. */
  .ui-select--sm .ui-select__field {
    height: 1.875rem;                   /* 30px */
    padding: 0.25rem 1.875rem 0.25rem 0.625rem;
    font-size: 0.8125rem;
  }
  .ui-select--md .ui-select__field {
    height: 2.25rem;                    /* 36px */
    padding: 0.375rem 2rem 0.375rem 0.75rem;
  }
  .ui-select--lg .ui-select__field {
    height: 2.75rem;                    /* 44px */
    padding: 0.625rem 2.25rem 0.625rem 1rem;
    font-size: 1rem;
  }

  .ui-select--disabled { opacity: 0.85; }
</style>
